/**
 * Alignment-box geometry for the design library's "mark the part that lines up" step
 * (docs/design-alignment-plan-v1.md § 3.1, § 4.2). Pure — no React, no DOM, no IO.
 *
 * Design files are trimmed to ink, so one satellite element — a logo in a corner, a vertical wordmark
 * down an edge — stretches the file's bounding box and drags the artwork off the shirt's centre line
 * when the press centres that box. The alignment box names the part that counts instead. It is stored
 * as FRACTIONS of the file box, so the same numbers work against the 300-DPI print PNG, the preview
 * WebP, and the frozen print inches on a batch item alike.
 *
 * `WHOLE_FILE` is the identity case: it reproduces today's placement exactly.
 */

/** A rectangle in file-fraction space: 0‥1 on each axis, origin at the file's top-left. */
export interface AlignBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Unset. Anchoring to this is algebraically identical to anchoring to the whole file. */
export const WHOLE_FILE: AlignBox = { x: 0, y: 0, w: 1, h: 1 };

/** The grid the operator's judgment is worth — finer than this is false precision on a heat press. */
export const ALIGN_SNAP_IN = 0.125;
/** Never let a drag collapse the box to nothing. */
export const MIN_BOX_IN = 0.5;
/** Alpha at or below this is transparent. Above a lossy re-encode's noise floor. */
export const INK_ALPHA_MIN = 8;
/** Slice depth for the profile. Tall enough to average out a ragged edge, short enough to see a band. */
export const SLICE_IN = 1.5;
/**
 * An empty band at least this tall between a top element and the body is a DETACHMENT, not a thin
 * stroke or an anti-aliased halo (plan § 13; the threshold M1 measured the library at).
 */
export const TOP_GAP_MIN_IN = 0.25;
/**
 * The detached element must be SMALL in absolute terms — a stray dot, a scratch, a thin swash — or it
 * is part of the composition and the design's real top edge. Measured against every live band
 * (§ 13, 2026-09-20): the two genuine cases are 0.02 in and 0.26 in tall; every false one was a brand
 * header or a title line 0.97–3.71 in tall sitting over a clean gap, exactly where a person would want
 * the design hung from. Half an inch sits between the two populations with room either side.
 */
export const TOP_GAP_MAX_ELEMENT_IN = 0.5;
/**
 * And small beside the body: a guard for tiny files where half an inch IS the composition. Element
 * height ÷ body height.
 */
export const TOP_GAP_MAX_ELEMENT_RATIO = 0.35;

const EPS = 1e-9;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Snap a fraction to the nearest ⅛ in of a file this many inches across. */
export function snapFraction(frac: number, spanIn: number): number {
  if (!(spanIn > 0)) return clamp01(frac);
  const inches = frac * spanIn;
  return clamp01((Math.round(inches / ALIGN_SNAP_IN) * ALIGN_SNAP_IN) / spanIn);
}

/** Keep a box inside the file and above the minimum extent, in fraction space. */
export function clampBox(box: AlignBox, fileWIn: number, fileHIn: number): AlignBox {
  const minW = fileWIn > 0 ? Math.min(1, MIN_BOX_IN / fileWIn) : 0.01;
  const minH = fileHIn > 0 ? Math.min(1, MIN_BOX_IN / fileHIn) : 0.01;
  const w = Math.min(1, Math.max(minW, box.w));
  const h = Math.min(1, Math.max(minH, box.h));
  return { x: clamp01(Math.min(box.x, 1 - w)), y: clamp01(Math.min(box.y, 1 - h)), w, h };
}

/** Move the whole box by a delta in fraction space, snapped and clamped. */
export function moveBox(
  box: AlignBox,
  dx: number,
  dy: number,
  fileWIn: number,
  fileHIn: number,
): AlignBox {
  return clampBox(
    {
      ...box,
      x: snapFraction(box.x + dx, fileWIn),
      y: snapFraction(box.y + dy, fileHIn),
    },
    fileWIn,
    fileHIn,
  );
}

export type BoxCorner = 'nw' | 'ne' | 'sw' | 'se';

/** Drag one corner. The opposite corner stays put, which is what makes a resize feel like a resize. */
export function resizeBox(
  box: AlignBox,
  corner: BoxCorner,
  dx: number,
  dy: number,
  fileWIn: number,
  fileHIn: number,
): AlignBox {
  const right = box.x + box.w;
  const bottom = box.y + box.h;
  let { x, y, w, h } = box;
  if (corner === 'nw' || corner === 'sw') {
    x = snapFraction(box.x + dx, fileWIn);
    w = right - x;
  } else {
    w = snapFraction(right + dx, fileWIn) - box.x;
  }
  if (corner === 'nw' || corner === 'ne') {
    y = snapFraction(box.y + dy, fileHIn);
    h = bottom - y;
  } else {
    h = snapFraction(bottom + dy, fileHIn) - box.y;
  }
  return clampBox({ x, y, w, h }, fileWIn, fileHIn);
}

/** The box in inches, plus where its centre sits measured from the file's left edge. */
export function boxInInches(box: AlignBox, fileWIn: number, fileHIn: number) {
  return {
    xIn: box.x * fileWIn,
    yIn: box.y * fileHIn,
    wIn: box.w * fileWIn,
    hIn: box.h * fileHIn,
    centreIn: (box.x + box.w / 2) * fileWIn,
  };
}

/**
 * How far the box's centre sits from the FILE's centre, in inches. Negative = the artwork sits left of
 * where the press would put it. This is the number that ranks the backfill worklist — measured across
 * the live library, the median is 0.05 in and the four confirmed failures run 0.29 → 0.78 in.
 */
export function offsetFromFileCentreIn(box: AlignBox, fileWIn: number): number {
  return (box.x + box.w / 2 - 0.5) * fileWIn;
}

/** Does this box actually narrow anything, or is it the whole file by another name? */
export function isWholeFile(box: AlignBox): boolean {
  return box.x <= EPS && box.y <= EPS && box.w >= 1 - EPS && box.h >= 1 - EPS;
}

export interface SliceProfile {
  /** The proposed alignment box — horizontal profile only; this is what seeds the create step. */
  box: AlignBox;
  /** Its centre offset from the file's centre, in inches (see `offsetFromFileCentreIn`). */
  offsetIn: number;
  /** How many slices carried ink — a proposal from one slice is not a profile. */
  slices: number;
  /**
   * A detached element above the main art, when the file has one (plan § 13). NEVER folded into
   * `box`: it is surfaced as a note + a one-click suggestion, and only seeds a box once it has run on
   * real intake without a false positive (§ 13 step 2).
   */
  topGap?: TopGap;
}

/** A small element sitting above the main art with an empty band between them — see `detectTopGap`. */
export interface TopGap {
  /** Height of the empty band separating the element from the body, in inches. */
  gapIn: number;
  /** The detached element's own height (top of ink → top of the gap), in inches. */
  elementHIn: number;
  /** Where the body starts, in inches from the file's top edge. */
  bodyTopIn: number;
  /** The horizontal proposal with its top edge moved down to the body's top — the suggestion to offer. */
  box: AlignBox;
}

/**
 * **Detect a detached element above the main art.**
 *
 * Nearly every press rule is `top`-anchored: the design's top edge hangs a fixed distance below the
 * collar. A star, a swash or a stray dot sitting above the lettering with clear space under it makes
 * the beam measure to THAT, and the lettering lands low by the gap plus the element's height — over
 * an inch on `Legends Never Die` (M1). The horizontal profile cannot see this, and a naive vertical
 * profile measures sparseness instead (42 files read "low", 2 actually were — plan § 11), so the
 * signal here is narrower and specific: an EMPTY row band, at least `TOP_GAP_MIN_IN` tall, with ink
 * above it that is SMALL — under `TOP_GAP_MAX_ELEMENT_IN` tall, and small beside the ink below it.
 * Wispy art has no empty band — it thins, it never breaks. A brand header or a title line breaks, but
 * it is an inch or more tall, and it IS the design's top edge: hanging the design from it is correct,
 * so the height cap refuses it (5 of the 7 designs the uncapped rule flagged were exactly this — § 13).
 * A footer mark under the body is refused the same way: everything above ITS gap is the artwork.
 *
 * ⚠️ There is deliberately NO "top 40 % of the file" zone, though M1 measured with one. The cap makes
 * it redundant (whatever sits above a low gap is the art, and is tall), and it would silence the one
 * case it can reach: a speck, then inches of nothing, then the art — the file box starts at the speck
 * however far down the art begins. A mutation pass proved the zone unreachable before it was removed.
 *
 * Candidates are tried from the LOWEST qualifying gap upward, so two stacked strays — a speck, then a
 * scratch — are both peeled, as long as everything above the gap stays under the cap.
 */
export function detectTopGap(
  alpha: ArrayLike<number>,
  widthPx: number,
  heightPx: number,
  fileHIn: number,
  horizontal: AlignBox,
): TopGap | null {
  if (widthPx <= 0 || heightPx <= 0 || fileHIn <= 0 || alpha.length < widthPx * heightPx)
    return null;
  const pxPerIn = heightPx / fileHIn;
  // Which rows carry ink at all.
  const inked = new Uint8Array(heightPx);
  for (let y = 0; y < heightPx; y++) {
    const row = y * widthPx;
    for (let x = 0; x < widthPx; x++) {
      if ((alpha[row + x] ?? 0) > INK_ALPHA_MIN) {
        inked[y] = 1;
        break;
      }
    }
  }
  let firstInk = -1;
  let lastInk = -1;
  for (let y = 0; y < heightPx; y++) {
    if (inked[y]) {
      if (firstInk < 0) firstInk = y;
      lastInk = y;
    }
  }
  if (firstInk < 0) return null;
  // Empty runs strictly BETWEEN ink, as [y0, y1) row ranges.
  const gaps: { y0: number; y1: number }[] = [];
  let y = firstInk;
  while (y <= lastInk) {
    if (inked[y]) {
      y++;
      continue;
    }
    const y0 = y;
    while (y <= lastInk && !inked[y]) y++;
    gaps.push({ y0, y1: y });
  }
  const minGapPx = TOP_GAP_MIN_IN * pxPerIn;
  const candidates = gaps.filter((g) => g.y1 - g.y0 >= minGapPx).sort((a, b) => b.y1 - a.y1); // lowest first
  for (const g of candidates) {
    const elementH = g.y0 - firstInk;
    const bodyH = lastInk + 1 - g.y1;
    if (elementH <= 0 || bodyH <= 0) continue;
    if (elementH / pxPerIn > TOP_GAP_MAX_ELEMENT_IN) continue;
    if (elementH / bodyH > TOP_GAP_MAX_ELEMENT_RATIO) continue;
    const yFrac = g.y1 / heightPx;
    return {
      gapIn: (g.y1 - g.y0) / pxPerIn,
      elementHIn: elementH / pxPerIn,
      bodyTopIn: g.y1 / pxPerIn,
      box: { x: horizontal.x, y: yFrac, w: horizontal.w, h: 1 - yFrac },
    };
  }
  return null;
}

/**
 * **Propose an alignment box by profiling the art in horizontal slices.**
 *
 * For each ~1.5-inch slice, take that slice's own ink bounding box; the median left and right edges
 * across slices are the artwork's rectangle. A satellite element occupies only a few slices, so the
 * median ignores it — which is exactly the geometry the press gets wrong.
 *
 * ⚠️ The BOX is horizontal only, on purpose. The same profile run vertically measures how SPARSE the
 * top of a design is, not whether it is misaligned: of 275 live band files, 42 have the median column
 * starting over half an inch below the file's top edge, but only 2 have a genuinely detached element up
 * there — the rest are wispy art whose top edge is entirely correct (plan § 11, M4). Proposing a
 * vertical narrowing from the profile would therefore be wrong far more often than right, so the box
 * keeps the full height. The one vertical shape that DOES fool a top-anchored beam — a small element
 * with an empty band under it — is detected separately and reported as `topGap` (§ 13), never folded
 * into `box`.
 *
 * ⚠️ And the proposal is a SUGGESTION, never applied on its own: reliable at the top of the ranking,
 * unreliable in the 0.2–0.4 in band where it tends to shave a legitimate edge element (M4).
 */
export function proposeAlignment(
  alpha: ArrayLike<number>,
  widthPx: number,
  heightPx: number,
  fileWIn: number,
  fileHIn: number,
): SliceProfile | null {
  if (widthPx <= 0 || heightPx <= 0 || alpha.length < widthPx * heightPx) return null;
  const pxPerIn = fileHIn > 0 ? heightPx / fileHIn : heightPx;
  const step = Math.max(1, Math.round(pxPerIn * SLICE_IN));
  const lefts: number[] = [];
  const rights: number[] = [];
  for (let y0 = 0; y0 < heightPx; y0 += step) {
    const y1 = Math.min(heightPx, y0 + step);
    let lo = widthPx;
    let hi = -1;
    for (let y = y0; y < y1; y++) {
      const row = y * widthPx;
      for (let x = 0; x < widthPx; x++) {
        // Indexed access is `number | undefined` under noUncheckedIndexedAccess; a missing sample
        // is transparent, which is the same decision as a below-threshold one.
        if ((alpha[row + x] ?? 0) <= INK_ALPHA_MIN) continue;
        if (x < lo) lo = x;
        if (x > hi) hi = x;
      }
    }
    if (hi < 0) continue;
    lefts.push(lo / widthPx);
    rights.push((hi + 1) / widthPx);
  }
  if (lefts.length === 0) return null;
  const median = (a: number[]) => {
    const s = [...a].sort((p, q) => p - q);
    return s[s.length >> 1] as number;
  };
  const x = median(lefts);
  const w = Math.max(median(rights) - x, 0);
  if (!(w > 0)) return null;
  const box = { x, y: 0, w, h: 1 };
  const topGap = detectTopGap(alpha, widthPx, heightPx, fileHIn, box);
  return {
    box,
    offsetIn: offsetFromFileCentreIn(box, fileWIn),
    slices: lefts.length,
    ...(topGap ? { topGap } : {}),
  };
}

/** One band's stored alignment as the API returns it, plus the raster it was marked against. */
export interface StoredAlignment extends AlignBox {
  forWidthPx?: number;
  forHeightPx?: number;
  setAt?: string;
  setBy?: string;
}

/**
 * Was this box marked against a DIFFERENT raster than the band now holds? A re-upload keeps the box
 * (clearing it would silently revert the design to file-box centring, which is the bug), so the only
 * honest thing to do is say so and let someone look.
 *
 * ⚠️ This compares pixel dimensions, which is a heuristic: art recomposed at the SAME dimensions reads
 * as fresh. It is a cheap true-positive signal, not a guarantee — there is no content hash to compare.
 */
export function alignmentIsStale(
  stored: StoredAlignment | undefined,
  band: { widthPx?: number | null; heightPx?: number | null } | undefined,
): boolean {
  if (!stored || !band) return false;
  const { forWidthPx, forHeightPx } = stored;
  if (forWidthPx == null || forHeightPx == null) return false;
  if (band.widthPx == null || band.heightPx == null) return false;
  return forWidthPx !== band.widthPx || forHeightPx !== band.heightPx;
}

/** Strip a stored alignment down to the geometry the canvas edits. */
export function toBox(stored: StoredAlignment | undefined): AlignBox {
  return stored ? { x: stored.x, y: stored.y, w: stored.w, h: stored.h } : WHOLE_FILE;
}

/** Do two boxes describe the same rectangle, to within float noise? */
export function sameBox(a: AlignBox, b: AlignBox): boolean {
  const EPS = 1e-9;
  return (
    Math.abs(a.x - b.x) < EPS &&
    Math.abs(a.y - b.y) < EPS &&
    Math.abs(a.w - b.w) < EPS &&
    Math.abs(a.h - b.h) < EPS
  );
}
